import { useState, FormEvent, useRef, useEffect } from "react";
import { SITE } from "../../../mocks/siteData";
import formsContent from "@/content/forms.json";

const FORM_URL = formsContent.leadForm.formUrl;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type LeadFormPayload = {
  access_key: string;
  subject: string;
  from_name: string;
  botcheck: string;
  Name: string;
  Email: string;
  Phone: string;
  "Service Requested": string;
  Message: string;
  "Page URL": string;
};

function useReveal(ref: React.RefObject<HTMLElement | null>, delay = 0) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          el.style.opacity = "1";
          el.style.transform = "translateY(0) translateX(0)";
        }, delay);
        obs.disconnect();
      }
    }, { threshold: 0.06 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay, ref]);
}

export default function LeadFormSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  useReveal(leftRef as React.RefObject<HTMLElement>, 0);
  useReveal(rightRef as React.RefObject<HTMLElement>, 180);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const botcheck = String(formData.get("botcheck") ?? "").trim();
    if (botcheck) return;

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const phone = String(formData.get("phone") ?? "").trim();
    const service = String(formData.get("service") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name) {
      setError("Please enter your name.");
      return;
    }

    if (!email && !phone) {
      setError("Please enter your phone number or email address.");
      return;
    }

    if (email && !EMAIL_PATTERN.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    const payload: LeadFormPayload = {
      access_key: formsContent.leadForm.web3formsAccessKey,
      subject: formsContent.leadForm.subject,
      from_name: formsContent.leadForm.fromName,
      botcheck,
      Name: name,
      Email: email,
      Phone: phone,
      "Service Requested": service,
      Message: message,
      "Page URL": window.location.href,
    };

    setLoading(true);
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        form.reset();
        setSubmitted(true);
      } else {
        setError(`${formsContent.leadForm.errorMessage} ${SITE.phone}`);
      }
    } catch {
      setError(`${formsContent.leadForm.networkErrorMessage} ${SITE.phone}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden scroll-mt-28"
      id="contact"
      style={{ background: "linear-gradient(160deg, #0A0A0A 0%, #111108 100%)" }}
    >
      {/* Background image — subtle */}
      <div className="absolute inset-0">
        <img
          src={formsContent.leadForm.backgroundImage}
          alt=""
          className="w-full h-full object-cover object-center opacity-[0.08]"
        />
      </div>
      {/* Gold glow — left */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 55% 70% at 20% 50%, rgba(196,144,42,0.06) 0%, transparent 65%)" }}
      />
      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C4902A]/25 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">

          {/* LEFT — Contact info */}
          <div
            ref={leftRef}
            className="lg:col-span-5"
            style={{ opacity: 0, transform: "translateX(-24px)", transition: "opacity 0.9s cubic-bezier(0.4,0,0.2,1), transform 0.9s cubic-bezier(0.4,0,0.2,1)" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#C4902A]" />
              <p className="text-[#C4902A] text-[11px] font-bold tracking-[0.3em] uppercase">{formsContent.leadForm.eyebrow}</p>
            </div>
            <h2
              className="font-serif text-white leading-tight mb-5"
              style={{ fontSize: "clamp(1.9rem, 3vw, 2.8rem)", fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {formsContent.leadForm.heading}
            </h2>
            <p className="text-white/45 leading-relaxed mb-10 text-sm max-w-sm">
              {formsContent.leadForm.description}
            </p>

            {/* Contact list */}
            <div className="space-y-5 mb-10">
              <a href={`tel:${SITE.phonePlain}`} className="flex items-center gap-4 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C4902A]/10 border border-[#C4902A]/20 group-hover:bg-[#C4902A]/20 transition-colors shrink-0">
                  <i className="ri-phone-line text-[#C4902A] text-lg"></i>
                </div>
                <div>
                  <p className="text-white/30 text-[10px] tracking-[0.2em] uppercase font-medium mb-0.5">Phone</p>
                  <p className="text-white font-semibold text-base">{SITE.phone}</p>
                </div>
              </a>

              <a href={`mailto:${SITE.email}`} className="flex items-center gap-4 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C4902A]/10 border border-[#C4902A]/20 group-hover:bg-[#C4902A]/20 transition-colors shrink-0">
                  <i className="ri-mail-line text-[#C4902A] text-lg"></i>
                </div>
                <div>
                  <p className="text-white/30 text-[10px] tracking-[0.2em] uppercase font-medium mb-0.5">Email</p>
                  <p className="text-white font-semibold text-sm">{SITE.email}</p>
                </div>
              </a>

              <a href={SITE.mapUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C4902A]/10 border border-[#C4902A]/20 group-hover:bg-[#C4902A]/20 transition-colors shrink-0">
                  <i className="ri-map-pin-line text-[#C4902A] text-lg"></i>
                </div>
                <div>
                  <p className="text-white/30 text-[10px] tracking-[0.2em] uppercase font-medium mb-0.5">Address</p>
                  <p className="text-white font-medium text-sm">{SITE.address}</p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C4902A]/10 border border-[#C4902A]/20 shrink-0">
                  <i className="ri-time-line text-[#C4902A] text-lg"></i>
                </div>
                <div>
                  <p className="text-white/30 text-[10px] tracking-[0.2em] uppercase font-medium mb-0.5">Hours</p>
                  <p className="text-white/65 text-sm">{SITE.hours.weekday}</p>
                  <p className="text-white/65 text-sm">{SITE.hours.weekend}</p>
                  <p className="text-[#C4902A] text-sm font-semibold">{SITE.hours.emergency}</p>
                </div>
              </div>
            </div>

            {/* Promo badge */}
            <div className="inline-flex items-center gap-2.5 bg-[#C4902A]/10 border border-[#C4902A]/25 rounded-full px-5 py-2.5">
              <i className="ri-price-tag-3-line text-[#C4902A] text-sm"></i>
              <span className="text-[#C4902A] text-xs font-semibold">{formsContent.leadForm.promoText}</span>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div
            ref={rightRef}
            className="lg:col-span-7"
            style={{ opacity: 0, transform: "translateY(24px)", transition: "opacity 0.9s cubic-bezier(0.4,0,0.2,1), transform 0.9s cubic-bezier(0.4,0,0.2,1)" }}
          >
            <div className="bg-white rounded-2xl p-8 lg:p-10 border border-white/10">
              {submitted ? (
                <div className="text-center py-14">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C4902A]/10 mx-auto mb-5">
                    <i className="ri-check-line text-[#C4902A] text-2xl"></i>
                  </div>
                  <h3 className="font-serif text-[#1A1A1A] text-2xl mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {formsContent.leadForm.successHeading}
                  </h3>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed max-w-xs mx-auto">
                    {formsContent.leadForm.successMessage}&nbsp;
                    <a href={`tel:${SITE.phonePlain}`} className="text-[#C4902A] font-semibold">{SITE.phone}</a>.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="park-lawn-contact-form"
                  className="space-y-5"
                >
                  <input
                    type="checkbox"
                    name="botcheck"
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                  />
                  <div className="mb-7">
                    <h3
                      className="font-serif text-[#1A1A1A] text-xl mb-1"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      {formsContent.leadForm.formHeading}
                    </h3>
                    <p className="text-[#8A8A8A] text-xs">{formsContent.leadForm.formDescription}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-[#8A8A8A] uppercase tracking-[0.18em] mb-2">{formsContent.leadForm.fields.name.label}</label>
                      <input
                        type="text"
                        name="name"
                        placeholder={formsContent.leadForm.fields.name.placeholder}
                        required
                        className="w-full border border-[#E5E2DD] rounded-lg px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#C5C2BD] focus:outline-none focus:border-[#C4902A] focus:ring-1 focus:ring-[#C4902A]/20 transition-all bg-[#FAFAF9]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-[#8A8A8A] uppercase tracking-[0.18em] mb-2">{formsContent.leadForm.fields.phone.label}</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder={formsContent.leadForm.fields.phone.placeholder}
                        className="w-full border border-[#E5E2DD] rounded-lg px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#C5C2BD] focus:outline-none focus:border-[#C4902A] focus:ring-1 focus:ring-[#C4902A]/20 transition-all bg-[#FAFAF9]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-[#8A8A8A] uppercase tracking-[0.18em] mb-2">{formsContent.leadForm.fields.email.label}</label>
                    <input
                      type="email"
                      name="email"
                      placeholder={formsContent.leadForm.fields.email.placeholder}
                      className="w-full border border-[#E5E2DD] rounded-lg px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#C5C2BD] focus:outline-none focus:border-[#C4902A] focus:ring-1 focus:ring-[#C4902A]/20 transition-all bg-[#FAFAF9]"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-[#8A8A8A] uppercase tracking-[0.18em] mb-2">{formsContent.leadForm.fields.service.label}</label>
                    <select
                      name="service"
                      className="w-full border border-[#E5E2DD] rounded-lg px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#C4902A] focus:ring-1 focus:ring-[#C4902A]/20 transition-all bg-[#FAFAF9]"
                    >
                      <option value="">{formsContent.leadForm.fields.service.placeholder}</option>
                      {formsContent.leadForm.fields.service.options.map((option) => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-[#8A8A8A] uppercase tracking-[0.18em] mb-2">{formsContent.leadForm.fields.message.label}</label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder={formsContent.leadForm.fields.message.placeholder}
                      className="w-full border border-[#E5E2DD] rounded-lg px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#C5C2BD] focus:outline-none focus:border-[#C4902A] focus:ring-1 focus:ring-[#C4902A]/20 transition-all resize-none bg-[#FAFAF9]"
                    />
                  </div>

                  {error && <p className="text-red-500 text-xs">{error}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#C4902A] text-white font-bold py-4 rounded-full hover:bg-[#d4a030] active:scale-[0.98] transition-all disabled:opacity-60 whitespace-nowrap tracking-wider text-sm shadow-lg shadow-[#C4902A]/20"
                  >
                    {loading ? formsContent.leadForm.loadingText : formsContent.leadForm.submitText}
                  </button>

                  <p className="text-center text-xs text-[#8A8A8A]">
                    {formsContent.leadForm.emergencyNote}&nbsp;
                    <a href={`tel:${SITE.phonePlain}`} className="text-[#C4902A] font-bold">{SITE.phone}</a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
